import { useEffect, useState } from "react";

import { createPortal } from "react-dom";

import { getLineWrapperGetPos } from "@/components/tiptap/utils/line-wrapper/lineWrapperGetPos";

import { LockIndicatorButton } from "./LockIndicatorButton";

import type { DocumentModel } from "@/models/DocumentModel";

type OpenState = {
  top: number;
  left: number;
  getPos: () => number | undefined;
};

/**
 * Opens the block menu for the static gutter lock icons that line wrappers
 * render (DOM-spec and React node views alike). PM selection redraws rebuild
 * or refocus node-view DOM, so the menu floats over the clicked icon from
 * outside the editor DOM (same idea as LineOptionButtonsOverlay).
 */
export function LockIndicatorsOverlay({ doc }: { doc: DocumentModel }) {
  const { tiptap } = doc.editor;
  const [openState, setOpenState] = useState<OpenState | null>(null);

  useEffect(() => {
    if (!tiptap) return;

    const root =
      tiptap.options.element instanceof HTMLElement
        ? tiptap.options.element
        : document.querySelector<HTMLElement>(".tiptap");
    if (!root) return;

    const indicatorFromEvent = (event: Event): HTMLElement | null =>
      event.target instanceof Element
        ? event.target.closest<HTMLElement>(".lock-indicator-static")
        : null;

    const onMouseDown = (event: MouseEvent) => {
      // Keep PM from moving the caret when the icon is pressed.
      if (indicatorFromEvent(event)) {
        event.preventDefault();
      }
    };

    const onClick = (event: MouseEvent) => {
      const indicator = indicatorFromEvent(event);
      if (!indicator) return;

      const getPos = getLineWrapperGetPos(indicator);
      if (!getPos) return;

      event.preventDefault();
      const { top, left } = indicator.getBoundingClientRect();
      setOpenState({ top, left, getPos });
    };

    root.addEventListener("mousedown", onMouseDown);
    root.addEventListener("click", onClick);

    return () => {
      root.removeEventListener("mousedown", onMouseDown);
      root.removeEventListener("click", onClick);
    };
  }, [tiptap]);

  useEffect(() => {
    if (!openState) return;

    const onScroll = (event: Event) => {
      // Scrolling moves the icon out from under the floating menu; close it.
      // Scrolls inside the menu panel itself are fine.
      if (event.target instanceof Element && event.target.closest('[role="menu"]')) return;
      setOpenState(null);
    };

    window.addEventListener("scroll", onScroll, true);
    return () => window.removeEventListener("scroll", onScroll, true);
  }, [openState]);

  if (!openState) {
    return null;
  }

  return createPortal(
    <div className="fixed z-10" style={{ top: openState.top, left: openState.left }}>
      <LockIndicatorButton
        doc={doc}
        getPos={openState.getPos}
        className="flex items-center select-none"
        autoOpen
        onOpenChange={(open) => {
          if (!open) setOpenState(null);
        }}
      />
    </div>,
    document.body,
  );
}
